// Small helpers for in-app sound + OS-level notifications on ticket events.
// Duplicated in apps/pos/src/lib/notify.ts — the two apps don't share a
// package, and this is small enough not to warrant one.
import { getAuthToken, syncApiUrl } from "@cliqsuite/db/powersync";
import { Capacitor } from "@capacitor/core";
import { PushNotifications, type PermissionStatus } from "@capacitor/push-notifications";

// The installed app (Android, iOS) has no Web Notifications: it asks and pushes through FCM instead.
const native = Capacitor.isNativePlatform();
const fromNative = (p: PermissionStatus): NotificationStatus => (p.receive === "granted" || p.receive === "denied" ? p.receive : "default");

// A single reused <audio> element, not `new Audio(src)` per call — mobile
// browsers (this was confirmed happening on real devices: the new-ticket
// sound played fine on desktop but silently failed specifically on
// mobile) only carry an "unlocked" audio state forward to *later*
// programmatic .play() calls (e.g. a new ticket arriving with nobody
// touching the screen) if the very first successful play happened on
// this same element, synchronously inside a real user gesture. A fresh
// `new Audio()` each time starts locked again every call — priming a
// throwaway instance doesn't unlock the one actually used later.
let sharedAudio: HTMLAudioElement | null = null;

function getSharedAudio(): HTMLAudioElement {
  if (!sharedAudio) sharedAudio = new Audio();
  return sharedAudio;
}

/**
 * Must be called synchronously from inside a real user gesture — the
 * notification toggle's onClick (see KitchenBoard). Plays
 * the shared element muted for an instant, purely to unlock it; nothing
 * audible happens. After this, playSound()'s later calls (triggered by a
 * live ticket arriving, not a click) keep working for the rest of the
 * page's life, since they reuse this same now-unlocked element.
 */
export function unlockAudio(): void {
  const audio = getSharedAudio();
  audio.muted = true;
  audio.src = "/sounds/new-ticket.mp3";
  void audio
    .play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
    })
    .catch(() => {
      // If even a gesture-driven play fails, there's nothing more we can
      // do — later alerts will likely fail too until the next gesture.
      audio.muted = false;
    });
}

export function playSound(src: string) {
  const audio = getSharedAudio();
  audio.muted = false;
  audio.src = src;
  audio.currentTime = 0;
  audio.play().catch(() => {
    // Autoplay still blocked this session — the visual flash still lands the point.
  });
}

export type NotificationStatus = "granted" | "denied" | "default" | "unsupported";

export function getNotificationStatus(): NotificationStatus {
  if (native) return "default"; // the real answer is async: checkNotificationStatus
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

export async function checkNotificationStatus(): Promise<NotificationStatus> {
  return native ? fromNative(await PushNotifications.checkPermissions()) : getNotificationStatus();
}

export async function requestNotificationPermission(): Promise<NotificationStatus> {
  if (native) return fromNative(await PushNotifications.requestPermissions());
  if (typeof Notification === "undefined") return Promise.resolve("unsupported");
  if (Notification.permission !== "default") return Promise.resolve(Notification.permission);
  return Notification.requestPermission();
}

// Not a secret — this is meant to ship in the browser bundle. Paired with
// VAPID_PRIVATE_KEY on sync-api (see services/sync-api/src/lib/webpush.ts);
// if you ever rotate these, update both places (see .env.production.example).
const VAPID_PUBLIC_KEY = "BDFxPS9HabykC91SXTgh-QDVcRJMytnHSusG1vXmXLrtDpZjuVPsGcdPOQt5mYn2QNO5mfmCYHjqHMwXrNsyUPc";

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Safe);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Subscribes this device to real Web Push (via the service worker's own
 * push/notificationclick handlers in sw.ts) and registers the subscription
 * with sync-api against this device's id — this is what lets a "new
 * ticket" alert land even when the PWA is fully closed, not just
 * backgrounded, since the OS wakes the service worker independently of
 * whether the page is running. Requires Notification permission already
 * granted; safe to call on every mount (subscribing when already
 * subscribed just returns the existing subscription — no re-prompt, no
 * duplicate).
 */
export async function subscribeToPush(deviceId: string): Promise<void> {
  if (native) return registerNative();
  if (Notification.permission !== "granted") return;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    await fetch(syncApiUrl("/push/subscribe"), {
      method: "POST",
      headers: { "Content-Type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
      body: JSON.stringify({ deviceId, ...subscription.toJSON() }),
    });
  } catch (err) {
    console.error("Push subscribe failed:", err);
  }
}

let nativeRegistered = false;

/** The installed app: an FCM token, sent to sync-api for this device (the device comes from the token). */
async function registerNative(): Promise<void> {
  if (nativeRegistered || (await PushNotifications.checkPermissions()).receive !== "granted") return;
  nativeRegistered = true;
  try {
    // Android: the channel sync-api's pushes name; high importance so a ticket sounds and pops up
    if (Capacitor.getPlatform() === "android") {
      await PushNotifications.createChannel({ id: "tickets", name: "New tickets", importance: 5, sound: "default", vibration: true, visibility: 1 });
    }
    await PushNotifications.removeAllListeners();
    await PushNotifications.addListener("registration", ({ value }) => {
      void fetch(syncApiUrl("/push/subscribe-fcm"), {
        method: "POST",
        headers: { "Content-Type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
        body: JSON.stringify({ token: value }),
      }).catch((err) => console.error("Push subscribe failed:", err));
    });
    await PushNotifications.addListener("registrationError", (err) => console.error("Push registration failed:", err.error));
    await PushNotifications.register();
  } catch (err) {
    nativeRegistered = false;
    console.error("Push subscribe failed:", err);
  }
}

// Alerts can be turned off on this display even once the OS allows them (the OS permission can't be
// taken back from inside the app): no chime, and sync-api stops sending to it.
const ALERTS_OFF_KEY = "cliqsuite_kds_alerts_off";

export function alertsOff(): boolean {
  try {
    return localStorage.getItem(ALERTS_OFF_KEY) === "1";
  } catch {
    return false;
  }
}

export async function setAlertsOff(off: boolean, deviceId: string): Promise<void> {
  try {
    if (off) localStorage.setItem(ALERTS_OFF_KEY, "1");
    else localStorage.removeItem(ALERTS_OFF_KEY);
  } catch {
    // no storage: it only lasts until the app restarts
  }
  if (!off) return subscribeToPush(deviceId);
  nativeRegistered = false; // turning back on registers again
  await fetch(syncApiUrl("/push/unsubscribe"), {
    method: "POST",
    headers: { "Content-Type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
    body: "{}",
  }).catch((err) => console.error("Push unsubscribe failed:", err));
}
